'use client';

/**
 * @module
 * @category Component
 */

import { Status } from 'BomdisoftComponents/chips/status';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { CheckRoundedIcon, CloseRoundedIcon, EditRoundedIcon } from 'BomdisoftComponents/icons/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import 'BomdisoftComponents/translated-text/styles.css';
import { cancel, edit, getLanguageFromLocale, getLanguageName, SupportedLanguageCode, save } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { ChangeEvent, useEffect, useState } from 'react';

/** Focuses the editor as it mounts. Replaces `autoFocus`, which is banned
 * because autofocus on page load disorients screen-reader users — but this
 * editor only exists once the user has pressed Edit, and not focusing it there
 * would make them hunt for the field they just asked for. */
const focusOnEdit = (element: HTMLInputElement | HTMLTextAreaElement | null): void => {
  if (element && document.activeElement !== element) element.focus();
};

type TranslatedTextProps = {
  text: string | null;
  languageCode: SupportedLanguageCode;
  onSave: (newValue: string | null, language: SupportedLanguageCode) => void;
  multiline?: boolean;
  className?: string;
};

export const TranslatedText = ({ text, languageCode, multiline = false, className, onSave }: TranslatedTextProps) => {
  const params = useParams();
  const currentLanguage = getLanguageFromLocale(params?.lang as string) ?? 'english';

  const [value, setValue] = useState(text);
  const [inEditMode, setInEditMode] = useState(false);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValue(event.target.value);
  const handleEdit = () => setInEditMode(true);
  const handleCancel = () => setInEditMode(false);
  const handleSave = () => {
    if (value !== text) onSave(value, languageCode);
    setInEditMode(false);
  };

  return (
    <div className={joinClasses('bomdisoft-translated-text', className)}>
      <div className='bomdisoft-translated-text-header'>
        <Status text={getLanguageName(languageCode)} variant='default' />
        <div className='bomdisoft-translated-text-controls'>
          {inEditMode ? (
            <>
              <Tooltip placement='top' title={cancel[currentLanguage]}>
                <button aria-label={cancel[currentLanguage]} className='bomdisoft-translated-text-icon-button' onClick={handleCancel} type='button'>
                  <CloseRoundedIcon className='bomdisoft-translated-text-cancel-icon' size={20} />
                </button>
              </Tooltip>
              <Tooltip placement='top' title={save[currentLanguage]}>
                <button aria-label={save[currentLanguage]} className='bomdisoft-translated-text-icon-button' onClick={handleSave} type='button'>
                  <CheckRoundedIcon className='bomdisoft-translated-text-save-icon' size={20} />
                </button>
              </Tooltip>
            </>
          ) : (
            <Tooltip placement='top' title={edit[currentLanguage]}>
              <button aria-label={edit[currentLanguage]} className='bomdisoft-translated-text-icon-button' onClick={handleEdit} type='button'>
                <EditRoundedIcon className='bomdisoft-translated-text-edit-icon' size={14} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <hr className='bomdisoft-translated-text-divider' />
      {inEditMode ? (
        multiline ? (
          <textarea className='bomdisoft-translated-text-input' onChange={handleChange} ref={focusOnEdit} required rows={5} value={value ?? ''} />
        ) : (
          <input className='bomdisoft-translated-text-input' onChange={handleChange} ref={focusOnEdit} required type='text' value={value ?? ''} />
        )
      ) : (
        <p className='bomdisoft-translated-text-text'>{text}</p>
      )}
    </div>
  );
};
